import React, { useEffect, useState, useRef } from "react";
import {
  DateSelectionCalendar,
  VenueHero,
  MobileVenueHero,
  EventDetailCard,
  MenuSection,
  AmbienceVibeSection,
  VenueSpecialtySection,
  VenueTimingsAndTnc,
  VenueContact,
  VenueUpcomingEventsSection,
  VenueOffers,
  useOfferById,
  GetReservationDrawer,
  usePub,
  usePubEvents,
  useLocationDetails,
  usePubConfig,
} from "@zillout/shared-components";
import {
  Box,
  Container,
  Text,
  Flex,
  Spinner,
  Center,
  useDisclosure,
  Icon,
  Grid,
  Link,
  Image,
  useMediaQuery,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaLongArrowAltRight } from "react-icons/fa";
import API from "../helpers/api";
import { menuTypeTheme } from "../helpers/values";
import { useTheme } from "@chakra-ui/react";
import { isLightColor } from "../helpers/utils";
import { useMetaPixelContext } from "../contexts/MetaPixelContext";
import {
  useTrackingSource,
  appendTrackingParams,
} from "../hooks/useTrackingSource";
import { env } from "../config/env";
import { ZILLOUT_WEB_BASE_URL } from "../config/urls";
import { IMG_URL } from "../utils";

export default function PlaceV2() {
  const pub_id = env.REACT_APP_PLACE_ID;

  // Capture UTM params and referrer for booking source attribution
  useTrackingSource();

  const theme = useTheme();
  const navigate = useNavigate();
  const { initPixel } = useMetaPixelContext();
  const [isMobile] = useMediaQuery("(max-width: 767px)");

  const defaultColors = theme.colors["dark"];
  const [pubColors, setPubColors] = useState(defaultColors);
  const [chainPubs, setChainPubs] = useState(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: pub, isLoading } = usePub(pub_id);
  const { data: offers, isLoading: offerLoading } = useOfferById(pub_id);
  const { data: eventsData, isLoading: isLoadingEvents } = usePubEvents(pub_id);
  const { data: pubConfig, isLoading: isConfigLoading } = usePubConfig(pub_id);
  const locationId = pub?.data?.pub_detail?.locationId;
  const { data: locations } = useLocationDetails(locationId);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const tncSection = useRef<HTMLDivElement>(null);

  const pubDetail = pub?.data?.pub_detail;
  const venueName = pubDetail?.name;
  const tagline = pubDetail?.metaData?.tnc?.menuTagLine || pubDetail?.type;

  // Get today's event (first event from the list)
  const todayEvent = pub?.data?.event_res;

  // Get upcoming events (exclude today's event)
  const allEvents = eventsData?.data || [];
  const upcomingEvents = allEvents.filter((event: any) => {
    if (!event.uuid || !todayEvent?.uuid) return true;
    return event.uuid !== todayEvent.uuid;
  });

  const onlineFood = pub?.data?.pub_detail?.metaData?.sections?.onlineFood;
  const orderingPlatforms = [
    "swiggy",
    "zomato",
    "doordash",
    "uber_eats",
    "grub_hub",
  ];

  const orderingLinksCount = onlineFood
    ? Object.entries(onlineFood).filter(
        ([key, value]) =>
          orderingPlatforms.includes(key) &&
          typeof value === "string" &&
          value.trim() !== ""
      ).length
    : 0;

  const hasOrderingLinks = orderingLinksCount > 0;

  useEffect(() => {
    const fetchChainPubs = async () => {
      if (pub?.data?.pub_detail?.chainId) {
        try {
          const chainId = pub.data.pub_detail.chainId;
          const response = await API.get(
            `/api/v1/rbzo/pubs/chains/listPubs?chainId=${chainId}`
          );
          setChainPubs(response?.data?.data);
        } catch (error) {
          console.error("Error fetching chain pubs:", error);
        }
      }
    };
    if (pub?.data && !isLoading) {
      fetchChainPubs();
    }
  }, [pub, isLoading]);

  useEffect(() => {
    if (pubConfig?.data) {
      // Init Meta Pixel
      const metaPixelId = pubConfig.data?.metadata?.metaPixelId;
      if (metaPixelId) initPixel(metaPixelId);

      const { menuType, menuColors } = pubConfig.data;
      const themeKey = menuTypeTheme[menuType]?.theme || "light";
      const fallbackTheme = theme.colors[themeKey];
      const menuTheme = menuTypeTheme[menuType] || {};

      const colors = {
        primary:
          menuColors?.secondaryContainer &&
          menuColors.secondaryContainer !== "#000000"
            ? menuColors.secondaryContainer
            : menuTheme.primary || fallbackTheme.primary,

        secondary:
          menuColors?.onPrimaryContainer &&
          menuColors.onPrimaryContainer !== "#000000"
            ? menuColors.onPrimaryContainer
            : menuTheme.secondary || fallbackTheme.secondary,

        bg:
          menuColors?.onSecondaryContainer &&
          menuColors.onSecondaryContainer !== "#000000"
            ? menuColors.onSecondaryContainer
            : menuTheme.bg || fallbackTheme.bg,

        card_bg:
          menuColors?.menuItemCard && menuColors.menuItemCard !== "#000000"
            ? menuColors.menuItemCard
            : menuTheme.secondary || fallbackTheme.card_bg,

        text:
          menuColors?.text && menuColors.text !== "#000000"
            ? menuColors.text
            : menuTheme.text || fallbackTheme.text,

        icon_color: isLightColor(
          menuColors?.onSecondaryContainer &&
            menuColors.onSecondaryContainer !== "#000000"
            ? menuColors.onSecondaryContainer
            : menuTheme.bg || fallbackTheme.icon_color
        )
          ? "black"
          : "white",

        card_border: pubColors.card_border || "#3A3D43",
      };

      setPubColors({
        ...fallbackTheme,
        ...colors,
      });
    }
  }, [pubConfig]);

  if (isLoading || isLoadingEvents) {
    return (
      <Container
        maxW="100%"
        p="0"
        background={pubColors.bg || "#111111"}
        minH="100vh"
        overflow="hidden"
      >
        <Center h="100vh">
          <Spinner size="xl" color={pubColors.primary || "#8155FE"} />
        </Center>
      </Container>
    );
  }

  return (
    <Box
      ref={containerRef}
      w="100%"
      p="0"
      h="100vh"
      position="relative"
      bg={pubColors.bg}
      color={pubColors.text}
      overflow="auto"
      overflowX={"hidden"}
    >
      {/* Content wrapper with relative positioning */}
      <Box position="relative" zIndex={1} pb={24}>
        {/* Mobile Hero - base and sm breakpoints */}
        {isMobile && (
          <MobileVenueHero
            pubDetail={pubDetail}
            venueName={venueName}
            onOpen={onOpen}
            eventsData={eventsData}
            eventsCount={eventsData?.data?.length || 0}
            pubColors={pubColors}
            pubId={pub_id}
            appContext={"experience"}
          />
        )}

        {/* Desktop Hero - md and larger */}
        <Box display={{ base: "none", md: "block" }}>
          <VenueHero
            pubDetail={pubDetail}
            venueName={venueName}
            tagline={tagline}
            onOpen={onOpen}
            eventsData={eventsData}
            pubColors={pubColors}
            pubId={pub_id}
          />
        </Box>

        {/* Today's Events */}
        {todayEvent && (
          <Box
            w="100%"
            bg="transparent"
            pt={[8, 8, 8, 16]}
            pb={[8, 8, 8, 16]}
            position="relative"
            zIndex={10}
          >
            <Container
              maxW={[
                "container.sm",
                "container.sm",
                "container.sm",
                "container.xl",
              ]}
              p={0}
            >
              <Text
                fontSize="3xl"
                mb={[5, 5, 5, 8]}
                px={5}
                textTransform="uppercase"
                letterSpacing={0.5}
                textAlign={["left", "left", "left", "center"]}
              >
                TODAY'S EVENTS
              </Text>
              <EventDetailCard
                event={todayEvent as any}
                pubColors={pubColors}
                pub_id={pub_id}
              />
            </Container>
          </Box>
        )}

        {/* Upcoming Events */}
        <VenueUpcomingEventsSection
          upcomingEvents={upcomingEvents}
          pubId={pub_id || ""}
          pubColors={pubColors}
          appContext={"experience"}
        />

        <VenueOffers
          offers={offers?.data || []}
          pubId={pub_id || ""}
          pubColors={pubColors}
        />

        {/* Menu Section */}
        <MenuSection
          menuImage={pubDetail?.metaData?.menuImage}
          menuCategories={pub?.data?.menu?.categories}
          pubName={venueName}
          pubColors={pubColors}
          appContext={"experience"}
          _pubId={pub_id || ""}
        />

        {/* Table Layout and Gallery */}
        <AmbienceVibeSection
          pubDetail={pubDetail}
          pubColors={pubColors}
          appContext={"experience"}
        />

        <Flex flexDir={{ base: "column", md: "row" }} gap={{ base: 0, md: 10 }}>
          {/* Venue Specialities */}
          <VenueSpecialtySection
            cardColor={pubDetail?.metaData?.specialityCardColor}
            description={pubDetail?.description}
            tags={pubDetail?.tags}
            pubColors={pubColors}
          />

          <Box id={"contact"} w={"100%"} ref={tncSection}>
            <VenueTimingsAndTnc pubDetails={pub?.data?.pub_detail} />
          </Box>
        </Flex>

        <Flex flexDir={{ base: "column", md: "row" }} gap={{ base: 0, md: 10 }}>
          {/* Other Outlets Section */}
          {chainPubs && Object.keys(chainPubs)?.length > 1 && (
            <Flex
              w={"100%"}
              flexDir={"column"}
              mt={[8, 8, 8, 10]}
              pb={[8, 8, 8, 16]}
              px={[4, 4, 4, 20]}
            >
              <Text fontSize={["2xl", "2xl", "2xl", "4xl"]} mb={[5, 5, 5, 10]}>
                Other Outlets By Us
              </Text>
              <Grid
                templateColumns={["1fr", "1fr", "1fr"]}
                alignItems={"flex-start"}
                gap={6}
                w={"100%"}
              >
                {Object.keys(chainPubs)?.map(
                  (key) =>
                    key !== pub_id && (
                      <Link
                        key={key}
                        href={appendTrackingParams(
                          `${ZILLOUT_WEB_BASE_URL}/pub/detail/${key}`
                        )}
                        target={"_blank"}
                      >
                        <Flex
                          alignItems={"center"}
                          gap={4}
                          justifyContent={"flex-start"}
                        >
                          <Text>{chainPubs[key]}</Text>
                          <Icon
                            color={pubColors.primary}
                            as={FaLongArrowAltRight}
                            boxSize={6}
                          />
                        </Flex>
                      </Link>
                    )
                )}
              </Grid>
            </Flex>
          )}
          {hasOrderingLinks && (
            <Flex
              w="100%"
              flexDir="column"
              mt={[8, 8, 8, 10]}
              pb={[8, 8, 8, 10]}
              px={[4, 4, 4, 10, 24]}
            >
              <Text
                fontSize={["2xl", "2xl", "3xl"]}
                color={pubColors.text}
                textTransform="uppercase"
                mb={6}
                letterSpacing="0.05em"
              >
                Order Online From
              </Text>
              <Flex
                mt={[2, 2, 2]}
                px={[2, 2, 2, 6]}
                w="100%"
                gap={4}
                flexWrap="wrap"
              >
                {Object.entries(onlineFood || {}).map(([key, url], index) =>
                  [
                    "swiggy",
                    "zomato",
                    "doordash",
                    "uber_eats",
                    "grub_hub",
                  ].includes(key) &&
                  typeof url === "string" &&
                  url.trim() !== "" ? (
                    <Link key={index} href={url} target="_blank">
                      <Image
                        src={`${IMG_URL}/icons/${key}.png`}
                        w={["80px", "80px", "80px", "100px"]}
                        alt={`${key} logo`}
                        borderRadius={10}
                      />
                    </Link>
                  ) : null
                )}
              </Flex>
            </Flex>
          )}
        </Flex>

        <VenueContact
          pubDetails={pub?.data?.pub_detail}
          locatoins={locations?.data}
          pubColors={pubColors}
        />

        {/* Registered Business Details Section */}
        <Flex
          w={"100%"}
          flexDir={"column"}
          mt={[4, 4, 4, 10]}
          mb={[5, 5, 5, 10]}
          pb={[8, 8, 8, 10]}
          px={[4, 4, 4, 10, 24]}
        >
          <Text
            fontSize={["2xl", "2xl", "3xl"]}
            color={pubColors.text}
            textTransform="uppercase"
            mb={6}
            letterSpacing="0.05em"
          >
            Registered Business Details
          </Text>
          <Flex
            alignItems={["flex-start"]}
            justifyContent={["flex-start"]}
            flexDir={["row"]}
            flexWrap={"wrap"}
            fontSize={["10px", "10px", "10px", "xs"]}
            fontWeight={400}
            gap={4}
            textAlign={"left"}
            w={"100%"}
          >
            <Box
              w={[
                "calc(50% - 0.5rem)",
                "calc(50% - 0.5rem)",
                "calc(50% - 0.5rem)",
                "calc(25% - 1rem)",
              ]}
            >
              <Text opacity={0.5} mb={1}>
                GST Number
              </Text>
              <Text>{pub?.data?.pub_detail?.gst || "--"}</Text>
            </Box>
            {pub?.data?.pub_detail?.placeName && (
              <Box
                w={[
                  "calc(50% - 0.5rem)",
                  "calc(50% - 0.5rem)",
                  "calc(50% - 0.5rem)",
                  "calc(25% - 1rem)",
                ]}
              >
                <Text opacity={0.5} mb={1}>
                  Place Name
                </Text>
                <Text>{pub?.data?.pub_detail?.placeName}</Text>
              </Box>
            )}
            <Box
              w={[
                "calc(50% - 0.5rem)",
                "calc(50% - 0.5rem)",
                "calc(50% - 0.5rem)",
                "calc(25% - 1rem)",
              ]}
            >
              <Text opacity={0.5} mb={1}>
                Registered Name
              </Text>
              <Text>
                {pub?.data?.pub_detail?.metaData?.registeredName || "--"}
              </Text>
            </Box>
            {pub?.data?.pub_detail?.metaData?.fssaiNumber && (
              <Box
                w={[
                  "calc(50% - 0.5rem)",
                  "calc(50% - 0.5rem)",
                  "calc(50% - 0.5rem)",
                  "calc(25% - 1rem)",
                ]}
              >
                <Text opacity={0.5} mb={1}>
                  FSSAI Number
                </Text>
                <Text>{pub?.data?.pub_detail?.metaData?.fssaiNumber}</Text>
              </Box>
            )}
          </Flex>
        </Flex>

        {/* Footer Section */}
        <Flex
          w={"100%"}
          alignItems={["flex-start", "flex-start", "flex-start", "center"]}
          justifyContent={["flex-start", "flex-start", "flex-start", "center"]}
          flexDir={"column"}
          pb={4}
          gap={2}
          mt={10}
          px={[6, 6, 6, 20]}
        >
          <Text
            fontSize={["7xl", "7xl", "7xl", "10xl"]}
            mb={[2, 2, 2, 5]}
            opacity={0.5}
            textAlign={["left", "left", "left", "center"]}
            fontFamily={"Rage Italic"}
            fontWeight={400}
            lineHeight={"60px"}
            maxW={"500px"}
          >
            {pub?.data?.pub_detail?.metaData?.tnc?.footer ||
              "Own the experience!"}
          </Text>
          <Flex
            fontSize={"10px"}
            fontWeight={400}
            gap={1.5}
            alignItems={"center"}
          >
            <Text opacity={0.5}>Crafted with </Text>
            <Icon as={FaHeart} color={"#ED1B24"} boxSize={4} />
            <Text opacity={0.5}> for limitless moments by Zillout</Text>
          </Flex>
        </Flex>
      </Box>

      {/* Reservation Drawer */}
      <GetReservationDrawer
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        pubColors={pubColors}
        drawerBody={
          <DateSelectionCalendar
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            pubColors={pubColors}
            onContinue={() => {
              if (selectedDate && pub_id) {
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const formattedDate = `${year}-${month}-${day}`;
                // const baseUrl = `https://zillout.com/pub/detail/events/${pub_id}?date=${formattedDate}`;
                // window.open(appendTrackingParams(baseUrl), "_self");
                navigate(`/pub/detail/events/${pub_id}?date=${formattedDate}`);
                onClose();
              }
            }}
          />
        }
      />
    </Box>
  );
}
